import { useEffect, useRef, useState, useCallback } from 'react';

type UseCountdownOptions = {
    duration: number;
    onComplete?: () => void;
};

type UseCountdownReturn = {
    remaining: number;
    isRunning: boolean;
    startCountdown: () => void;
    stopCountdown: () => void;
    resetCountdown: () => void;
    clearCountdown: () => void;
};

export function useCountdown({ duration, onComplete }: UseCountdownOptions): UseCountdownReturn {
    const [remaining, setRemaining] = useState(duration);
    const [isRunning, setIsRunning] = useState(false);
    const intervalRef = useRef<number | null>(null);
    const onCompleteRef = useRef(onComplete);
    const isCancelledRef = useRef(false);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    const stopCountdown = useCallback(() => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsRunning(false);
    }, []);

    const resetCountdown = useCallback(() => {
        stopCountdown();
        isCancelledRef.current = false;
        setRemaining(duration);
    }, [duration, stopCountdown]);

    const clearCountdown = useCallback(() => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        isCancelledRef.current = true;
        setRemaining(0);
        setIsRunning(false);
    }, []);

    const startCountdown = useCallback(() => {
        clearCountdown();
        isCancelledRef.current = false;
        setRemaining(duration);
        setIsRunning(true);

        intervalRef.current = window.setInterval(() => {
            setRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current!);
                    intervalRef.current = null;
                    setIsRunning(false);

                    setTimeout(() => {
                        if (!isCancelledRef.current) {
                            onCompleteRef.current?.();
                        }
                    }, 0);

                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [duration, clearCountdown]);

    useEffect(() => {
        return () => {
            if (intervalRef.current !== null) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return {
        remaining,
        isRunning,
        startCountdown,
        stopCountdown,
        resetCountdown,
        clearCountdown,
    };
}
